import { FC, useMemo } from 'react';

import { Stack, tv } from '@eluve/components';
import {
  AUDIO_LEVEL_END_FACTOR,
  AUDIO_LEVEL_GAIN,
  AUDIO_LEVEL_GAMMA,
  AUDIO_LEVEL_START_BIN,
} from '@eluve/mic-utils';

const waveformVariant = tv({
  slots: {
    base: '',
    bar: 'max-h-full rounded-full transition-[height] duration-100 ease-linear',
  },
  variants: {
    size: {
      sm: { base: 'h-4 gap-0.5', bar: 'min-h-0.5 w-0.5' },
      md: { base: 'h-6 gap-0.75', bar: 'min-h-0.75 w-0.75' },
      lg: { base: 'h-8 gap-1', bar: 'min-h-1 w-1' },
    },
    variant: {
      default: { bar: 'bg-text-tertiary' },
      brand: { bar: 'bg-text-indigo-subtle' },
      inverse: { bar: 'bg-white' },
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
});

export interface WaveformProps {
  barCount?: number;
  data?: number[];
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'brand' | 'inverse';
}

export const Waveform: FC<WaveformProps> = ({
  barCount = 18,
  data = [],
  size = 'md',
  variant = 'default',
}) => {
  const { base, bar } = waveformVariant({ size, variant });

  const groupSize = useMemo(() => {
    if (data.length === 0) {
      return null;
    }

    const endIndex = Math.floor(data.length / AUDIO_LEVEL_END_FACTOR);
    const relevantLength = endIndex - AUDIO_LEVEL_START_BIN;
    return Math.floor(relevantLength / barCount);
  }, [data.length, barCount]);

  const barHeights = useMemo(() => {
    if (!groupSize) {
      return Array(barCount).fill(0);
    }

    const heights = new Array(barCount);

    for (let barIndex = 0; barIndex < barCount; barIndex++) {
      const groupStart = AUDIO_LEVEL_START_BIN + barIndex * groupSize;
      const groupEnd = Math.min(groupStart + groupSize, data.length);

      let sum = 0;
      let count = 0;

      for (let i = groupStart; i < groupEnd; i++) {
        sum += (data[i] ?? 0) / 255;
        count++;
      }

      const avg = count > 0 ? Math.min(sum / count, 1) : 0;
      heights[barIndex] = Math.min(
        Math.pow(avg, AUDIO_LEVEL_GAMMA) * 100 * AUDIO_LEVEL_GAIN,
        100,
      );
    }

    return heights;
  }, [data, barCount, groupSize]);

  return (
    <Stack
      className={base({ size, variant })}
      align="center"
      justify="center"
      fullWidth={false}
      direction="horizontal"
    >
      {barHeights.map((height, i) => (
        <div
          key={i}
          className={bar({ size, variant })}
          style={{ height: `${height}%` }}
        />
      ))}
    </Stack>
  );
};
